import { Avatar, MenuItem, Rating, Select } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { GovIdVerifiedBadge } from '@src/components/common/gov-id-verified-badge/gov-id-verified-badge';
import { Button } from '@src/components/ui/button/button';
import { formatDateTime } from '@src/utils/date/formatDateTime';
import React, { useMemo, useRef, useState } from 'react';
import { useExpandableTruncation } from '@src/components/hooks/useExpandableTruncation';
import { cn } from '@src/utils/ui';
import { SubmitButton } from '@src/components/ui/react-hook-form/submit-button/submit-button';
import { Form } from '@src/components/ui/react-hook-form/form/form';
import { useWatch } from 'react-hook-form';
import { sortBy } from 'lodash';
import PropTypes from 'prop-types';

function StorefrontReviews(props) {
    const [sort, setSort] = useState('highest-rating');

    const merchantFeedback = useMemo(() => {
        const records = props.merchantFeedback.itemReviews || [];

        switch (sort) {
            case 'newest':
                return sortBy(records, r => -new Date(r.ratingCreated).getTime());

            case 'highest-rating':
                return sortBy(records, r => -r.customerRating);

            case 'lowest-rating':
                return sortBy(records, r => r.customerRating);

            default:
                return records;
        }
    }, [sort, props.merchantFeedback]);

    const handleSubmit = ({ reviews }, { setValue }) => {
        setValue('reviews', merchantFeedback.slice(0, reviews.length + 5));
    };

    return (
        <Form
            key={sort}
            onSubmit={handleSubmit}
            defaultValues={{ reviews: merchantFeedback.slice(0, 5) }}
            className='tw-pt-[32px] tw-space-y-[24px]'
        >
            <div className='tw-flex tw-gap-[8px] tw-items-center'>
                <div className='tw-text-[16px] tw-text-[#8B8B8B]'>Sort by:</div>
                <Select className='tw-grow' value={sort} onChange={e => setSort(e.target.value)} size='small'>
                    <MenuItem value='newest'>Newest</MenuItem>
                    <MenuItem value='highest-rating'>Highest Rating</MenuItem>
                    <MenuItem value='lowest-rating'>Lowest Rating</MenuItem>
                </Select>
            </div>
            <div className='tw-space-y-[16px]'>
                <ReviewList />
                <Buttons feedback={props.merchantFeedback} />
            </div>
        </Form>
    );
}

const ReviewList = () => {
    const reviews = useWatch({ name: 'reviews' });

    if (reviews.length < 1) {
        return (
            <div className='empty-page-sec tw-border tw-border-[rgba(0, 0, 0, 0.12)] tw-p-[16px]'>
                <img src='/assets/images/chats-circle.svg' className='tw-m-auto' alt='empty reviews' />
                <div className='empty-page-title'>No reviews yet</div>
                <div className='empty-page-message'>Be the first one to share your experience and help others.</div>
            </div>
        );
    }

    return (
        <div className='tw-space-y-[16px]'>
            {reviews.map(review => (
                <ReviewCard key={review.id} {...review} />
            ))}
        </div>
    );
};

function ReviewCard({
    itemName,
    mediaUrl,
    customerName,
    customerRating,
    ratingCreated,
    ratingMessage,
    isGovIdVerified
}) {
    const descRef = useRef(null);
    const { isTruncated, isExpanded, toggleExpand } = useExpandableTruncation(descRef);

    return (
        <div className='tw-space-y-[4px] tw-bg-[#ECEFF1] tw-border tw-border-[rgba(0, 0, 0, 0.12)] tw-p-[16px]'>
            <div className='tw-flex tw-gap-[8px]'>
                <Avatar src={mediaUrl} />
                <div className='tw-space-y-[16px] tw-grow tw-w-[1px]'>
                    <div className='tw-flex lg:tw-gap-[8px] tw-flex-col lg:tw-flex-row lg:tw-items-center'>
                        <div className='tw-flex tw-items-center tw-gap-[8px]'>
                            <div>
                                <div className='tw-font-medium'>{customerName}</div>
                                <div className='tw-text-[#5A5A5A] tw-text-[14px]'>
                                    {formatDateTime(ratingCreated, 'MM/DD/YYYY')}
                                </div>
                            </div>
                            {isGovIdVerified ? <GovIdVerifiedBadge className='tw-w-[24px] tw-aspect-square' /> : null}
                        </div>
                        <div className='lg:tw-self-stretch tw-grow tw-flex lg:tw-justify-end tw-items-start'>
                            <Rating name='tdp-rating' value={customerRating} readOnly />
                        </div>
                    </div>

                    <div>
                        <div className='tw-text-[#5A5A5A] tw-font-[500] tw-text-[16px]'>{itemName}</div>
                        <div className='tw-space-y-[4px]'>
                            <div
                                ref={descRef}
                                className={cn(
                                    'tw-text-[16px] tw-leading-[24px] tw-tracking-[0] tw-text-[#5A5A5A] tw-transition-all tw-break-words',
                                    !isExpanded && 'tw-line-clamp-4',
                                    'tw-whitespace-normal tw-break-words'
                                )}
                            >
                                {ratingMessage}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            {isTruncated && (
                <div className='tw-flex tw-justify-center tw-w-full'>
                    <Button
                        variant='text'
                        onClick={toggleExpand}
                        className='tw-text-[#5A5A5A] !tw-text-[14px] !tw-capitalize'
                        startIcon={
                            <KeyboardArrowDownIcon
                                size={24}
                                className='tw-transition-transform'
                                style={{ transform: isExpanded ? 'rotate(180deg)' : 'rotate(0)' }}
                            />
                        }
                    >
                        {isExpanded ? 'Show Less' : 'Show More'}
                    </Button>
                </div>
            )}
        </div>
    );
}

function Buttons({ feedback }) {
    const reviews = useWatch({ name: 'reviews' });
    if (!reviews.length) return null;

    const handleScrollTop = () => {
        window.scrollTo({
            top: 0,
            left: 0,
            behavior: 'auto'
        });
    };

    return (
        <div className='tw-flex tw-flex-col tw-gap-[8px] tw-items-center'>
            {feedback.TotalRecords <= reviews.length ? null : (
                <SubmitButton variant='contained'>SHOW MORE REVIEWS</SubmitButton>
            )}
            <Button variant='text' onClick={handleScrollTop}>
                BACK TO TOP
            </Button>
        </div>
    );
}

StorefrontReviews.propTypes = {
    merchantFeedback: PropTypes.shape({
        TotalRecords: PropTypes.number,
        itemReviews: PropTypes.arrayOf(
            PropTypes.shape({
                id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
                itemName: PropTypes.string,
                mediaUrl: PropTypes.string,
                customerName: PropTypes.string,
                customerRating: PropTypes.number,
                ratingCreated: PropTypes.string,
                ratingMessage: PropTypes.string,
                isGovIdVerified: PropTypes.bool
            })
        )
    }).isRequired
};

ReviewList.propTypes = {};

ReviewCard.propTypes = {
    itemName: PropTypes.string,
    mediaUrl: PropTypes.string,
    customerName: PropTypes.string,
    customerRating: PropTypes.number,
    ratingCreated: PropTypes.string,
    ratingMessage: PropTypes.string,
    isGovIdVerified: PropTypes.bool
};

Buttons.propTypes = {
    feedback: PropTypes.shape({
        TotalRecords: PropTypes.number,
        itemReviews: PropTypes.array
    })
};

export default StorefrontReviews;
